import { getTheme, IColumn, mergeStyles, ScrollToMode } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPFontIcon, IPIconButton, IPLabel, IPScrollablePane, IPShimmeredDetailsList, IPText } from 'baseComponents';
import { IPDetailsListLayoutMode } from 'baseComponents/DetailsList/IPDetailsList';
import { IPScrollbarVisibility } from 'baseComponents/ScrollablePane/IPScrollablePane';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPSelectionMode } from 'baseComponents/Utils/Selection';
import HrefLink from 'components/Links/HrefLink';
import EntityPicker from 'components/Pickers/EntityPicker';
import { getEntity, getEntityName, onRenderDetailsHeaderNoPaddingTopGlobal } from 'globalFunctions';
import {
  deleteIcon,
  editIcon,
  getFontIconColorPrimary,
  globalStackTokensGapSmall,
  globalTextStylesBold,
  globalTextStylesMarginTopLabelPanel,
  linkIcon,
  openExtIcon,
  switchIcon,
} from 'globalStyles';
import { TFunction } from 'i18next';
import Asset from 'models/asset/asset';
import { AuthSchemaLineOperation } from 'models/auth/authSchemaLine';
import Control from 'models/control';
import Entity, { EntityTypes } from 'models/entity';
import Objective from 'models/objective/objective';
import Process from 'models/process/process';
import ProcessingActivity from 'models/processingActivity';
import Risk from 'models/risk';
import Risks from 'models/risks';
import Task, { TaskTypes } from 'models/tasks/task';
import {
  applyTaskFollowUpSummary,
  areTaskRelationsDifferent,
  isSameTaskRelation,
  TaskRelation,
  TaskRelationship,
  TaskRelationshipDirection,
} from 'models/tasks/taskRelation';
import Tasks from 'models/tasks/tasks';
import Theme from 'models/theme';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGetAssets } from 'services/Api/assetService';
import { apiGetControls } from 'services/Api/controlService';
import { apiGetObjectives } from 'services/Api/objectiveService';
import { apiGetProcessingActivities } from 'services/Api/processingActivityService';
import { apiGetProcesses } from 'services/Api/processService';
import { apiGetRisks } from 'services/Api/riskService';
import { apiGetSingleTask, apiGetTaskContext, apiGetTasks } from 'services/Api/taskService';
import { apiGetThemes } from 'services/Api/themeService';
import { apiRequest } from 'services/Auth/authConfig';
import { hasUserDataPermission } from 'services/Auth/featurePermissions';
import { toLocaleDateTimeShort } from 'utils/datetime';
import { sortOnEntity, sortOnString } from 'utils/sorting';
import { navigateToExternalUrl } from 'utils/url';
import { TaskFollowUpLinkDialog } from '../FollowUp/TaskFollowUpLinkDialog';
import SingleTask from '../SingleTask';
import { canUpdateTaskField, TaskFieldTypes } from '../TaskAuthHelper';
import { TaskRelationLinkWithHoverCard } from './TaskRelationLinkWithHoverCard';

const editOnHoverClass = mergeStyles({
  opacity: 0,
  transition: 'opacity 120ms ease',
  selectors: {
    '.ms-DetailsRow:hover &': { opacity: 1 },
    ':focus-within': { opacity: 1 },
  },
});

interface ITaskContextProps {
  orgTask: Task;
  task: Task;
  updateTask?: (task: Task) => void;
  updateTaskForForm?: (task: Task, validateForm: boolean) => void;
  onCancel?: () => boolean;
  onClickFollowUp?: () => void;
  onLinkFollowUpToTemplate?: (relation: TaskRelation, template: Task) => Promise<void>;
  onCreateEmptyFollowUpTask?: (relation: TaskRelation) => Promise<void>;
  allowEdit: boolean;
  allowFollowUpEdit?: boolean;
  hideHeaders?: boolean;
  windowLevel?: number;
  navigateExternal?: boolean;
  //sizes to its content for a host that scrolls itself: no fill, no inner scroll pane, the title
  //only when there is something to list, and the picker's data fetched on mount so its first
  //keystroke answers at once
  fitContent?: boolean;
  //the heading over the list; the generic "related to" text when not given
  title?: string;
}

export const TaskContext = (props: ITaskContextProps) => {
  const { t } = useTranslation(['task', 'translation', 'asset', 'risk', 'control', 'theme', 'objective', 'process']);
  const appContext = useContext(AppContext);
  const [entities, setEntities] = useState<Entity[]>([]);
  const [allEntities, setAllEntities] = useState<Entity[]>([]);
  const [isLoadingTaskRelations, setIsLoadingTaskRelations] = useState<boolean>(false);
  const [taskRelations, setTaskRelations] = useState<TaskRelation[]>([]);
  const [linkRelation, setLinkRelation] = useState<TaskRelation | undefined>(undefined);
  const [isLoadingPicker, setIsLoadingPicker] = useState<boolean>(false);
  const [showRelatedTask, setShowRelatedTask] = useState<Task | undefined>(undefined);
  // A single number suffices because all add/remove operations are synchronous (no concurrent overlap).
  // If async operations are introduced, convert to Set<number> for concurrent safety.
  const [isBusyAddingRemoving, setIsBusyAddingRemoving] = useState<number>(0);
  const [sortedTaskContext, setsortedTaskContext] = useState<(Entity | TaskRelation)[]>([]);
  const [newlyAddedItem, setNewlyAddedItem] = useState<Entity | TaskRelation | undefined>(undefined);
  const [currentTaskId, setCurrentTaskId] = useState<number>(0);
  const theme = getTheme();

  // Ref to hold callbacks used inside getColumnsContext useMemo.
  // This avoids stale closures: onRender handlers read from the ref at click time,
  // so they always invoke the latest callback without needing to re-memoize columns.
  type ColumnCallbacksRef = {
    onRemove: (item: Entity | TaskRelation) => void;
    onLinkTaskRelation: (relation: TaskRelation) => void;
    onSwitchTaskRelation: (relation: TaskRelation) => void;
    loadRelatedTaskFromRelation: (relation: TaskRelation) => Promise<void>;
  };
  const columnCallbacksRef = useRef<ColumnCallbacksRef>({} as ColumnCallbacksRef);

  //Derived, not state: allowEdit can change after the first render - an audit execution task learns
  //asynchronously that its audit is completed, which locks this tab - and a state initializer would
  //keep the value it was mounted with. Every other caller passes a constant, so nothing else changes.
  const canEdit =
    (props.allowEdit || props.allowFollowUpEdit === true) &&
    hasUserDataPermission(appContext, [props.task.authSchemaId], AuthSchemaLineOperation.Update);
  const canUpdate = props.allowEdit && canUpdateTaskField(props.task, canEdit, TaskFieldTypes.Context, appContext);
  const canUpdateFollowUp =
    (props.allowEdit || props.allowFollowUpEdit === true) &&
    canUpdateTaskField(props.task, canEdit, TaskFieldTypes.Context, appContext);
  const canAddTasks =
    props.task.taskType !== TaskTypes.Template &&
    props.task.taskType !== TaskTypes.EventTemplate &&
    !(props.task.taskType === TaskTypes.Monitoring && props.task.isSeries()) &&
    !(props.windowLevel && props.windowLevel >= 1);
  const canUseContextPicker = canUpdate || (canUpdateFollowUp && canAddTasks);
  const isFollowUpOnlyPicker = canUpdateFollowUp && !canUpdate;

  const canEditTaskRelation = (relation: TaskRelation | undefined): boolean => {
    if (!relation) return false;
    if (!canUpdateFollowUp) return false;
    if (relation.relationship !== TaskRelationship.FollowUp) return false;

    return relation.direction(props.task.taskId) === TaskRelationshipDirection.To;
  };

  const getTaskRelationBusyKey = (relation: TaskRelation): number => {
    return relation.taskTaskId || relation.taskIdTo || relation.taskIdFrom;
  };

  const updateTaskRelations = (newRelations: TaskRelation[], baseTask?: Task) => {
    setTaskRelations(newRelations);

    if (props.updateTask) {
      const newTask = baseTask ?? props.task.clone();
      newTask.taskRelations = newRelations;
      applyTaskFollowUpSummary(newTask);
      props.updateTask(newTask);
    }
  };

  useEffect(() => {
    const sortedTaskRelations = [...taskRelations].sort((a, b) =>
      sortOnString(a.task(props.task.taskId)?.name ?? '', b.task(props.task.taskId)?.name ?? ''),
    );
    const sortedEntities = [...entities].sort((a, b) => sortOnEntity(a, b)).filter((e) => !e.isDeleted);
    const newSortedTaskContext = [...sortedTaskRelations, ...sortedEntities];

    setsortedTaskContext(newSortedTaskContext);
  }, [taskRelations, entities, props.task]);

  useEffect(() => {
    if (props.task.taskId !== currentTaskId) {
      setCurrentTaskId(props.task.taskId);

      const newEntities: Entity[] = props.task.relatedEntities ? props.task.relatedEntities.map((e) => e.clone()) : [];

      if (props.task.taskId !== -1) {
        loadData(newEntities);
        // This is an existing task, so the data has already been loaded.
      } else {
        //this is a new task
        loadDataFromTemplate(newEntities);
        const newRelations: TaskRelation[] = props.task.taskRelations.map((r) => r.clone());
        setTaskRelations(newRelations);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.task]);

  useEffect(() => {
    // If the task was just updated, and the relations no longer match the props, update the state.
    if (areTaskRelationsDifferent(props.task.taskRelations, taskRelations)) {
      const newRelations: TaskRelation[] = props.task.taskRelations.map((r) => r.clone());

      if (newRelations.length === taskRelations.length + 1) {
        const newestRelations = newRelations.filter(
          (newRelation) => !taskRelations.some((relation) => isSameTaskRelation(relation, newRelation)),
        );
        if (newestRelations.length === 1) {
          setNewlyAddedItem(newestRelations[0]);
        }
      }

      setTaskRelations(newRelations);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.task.taskRelations]);

  // An entity the host added to the task while this tab is open (the task assistant's context proposal) joins the
  // list, and one the tab removed before comes back. The tab's own adds and removes leave nothing to do here.
  useEffect(() => {
    const changed = (props.task.relatedEntities ?? []).filter((related) => {
      if (related.isDeleted) return false;
      const listed = entities.find((e) => e.typeOfEntity === related.typeOfEntity && e.entityId === related.entityId);

      return !listed || listed.isDeleted;
    });
    if (changed.length === 0) return;

    const newEntities = entities
      .filter((e) => !changed.some((c) => c.typeOfEntity === e.typeOfEntity && c.entityId === e.entityId))
      .concat(changed.map((e) => e.clone()));
    setEntities(newEntities);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.task.relatedEntities]);

  const loadDataFromTemplate = async (existingEntities: Entity[]) => {
    try {
      const newEntities: Entity[] = [...existingEntities];

      if (props.task.templateId) {
        //Load context from the template
        setIsLoadingTaskRelations(true);
        const accessToken = await appContext.getAccessToken(apiRequest.scopes);
        const context = await apiGetTaskContext(props.task.templateId, accessToken, appContext);

        //create entities from the context
        const newRelatedEntities: Entity[] = [];
        newRelatedEntities.push(...context.assets.map((a) => getEntity(a)));
        newRelatedEntities.push(...context.risks.map((a) => getEntity(a)));
        newRelatedEntities.push(...context.controls.map((a) => getEntity(a)));
        newRelatedEntities.push(...context.themes.map((a) => getEntity(a)));
        newRelatedEntities.push(...context.processes.map((a) => getEntity(a)));
        newRelatedEntities.push(...context.objectives.map((a) => getEntity(a)));
        newRelatedEntities.push(...context.processingActivities.map((a) => getEntity(a)));

        //add new entities to the task. There could already be entities in the task from:
        // - creation of a new task from a specific context e.g. Asset
        // - user added context
        for (const newEntity of newRelatedEntities) {
          if (
            !existingEntities.some(
              (e) => e.typeOfEntity === newEntity.typeOfEntity && e.entityId === newEntity.entityId,
            )
          ) {
            newEntities.push(newEntity);
          }
        }
      }

      //make sure that all related entities are set into the arrays with Ids and updated on the task
      newEntities.sort((a, b) => sortOnEntity(a, b));
      setEntities(newEntities);
      addToTask(newEntities);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoadingTaskRelations(false);
    }
  };

  const addToExistingEntities = (
    existingEntities: Entity[],
    entitiesToAdd: Control[] | Theme[] | Risk[] | Process[] | Objective[] | Asset[] | ProcessingActivity[],
  ) => {
    entitiesToAdd.forEach((entity) => {
      const newEntity = getEntity(entity);
      const matchingEntity = existingEntities.some(
        (e) => e.typeOfEntity === newEntity.typeOfEntity && e.entityId === newEntity.entityId,
      );
      if (!matchingEntity) existingEntities.push(newEntity);
    });
  };

  const loadData = async (existingEntities: Entity[]) => {
    if (isLoadingTaskRelations || !props.task) return;

    try {
      setIsLoadingTaskRelations(true);

      //get context from the back-end
      //add them to the existing context in the task (where they do not exist yet)
      //push everything back to the task
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const result = await apiGetTaskContext(props.task.taskId, accessToken, appContext);

      addToExistingEntities(existingEntities, result.controls);
      addToExistingEntities(existingEntities, result.themes);
      addToExistingEntities(existingEntities, result.risks);
      addToExistingEntities(existingEntities, result.assets);
      addToExistingEntities(existingEntities, result.processes);
      addToExistingEntities(existingEntities, result.objectives);
      addToExistingEntities(existingEntities, result.processingActivities);

      existingEntities.sort((a, b) => sortOnEntity(a, b));
      setEntities(existingEntities);
      const loadedRelations = result.taskRelations ?? [];
      const newTask = props.task.clone();
      newTask.addEntities(existingEntities);
      newTask.relatedEntities = existingEntities.map((entity) => entity.clone());
      updateTaskRelations(loadedRelations, newTask);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoadingTaskRelations(false);
    }
  };

  const pushFulfilledData = (
    localeFilename: string,
    _allEntities: Entity[],
    _dataToPush: PromiseSettledResult<
      Control[] | Process[] | Risks | Theme[] | Process[] | Objective[] | Asset[] | Tasks | ProcessingActivity[]
    >,
  ): string => {
    let errorMessage = '';
    if (_dataToPush.status === 'fulfilled') {
      _dataToPush.value.forEach(
        (_entity: Control | Process | Risk | Theme | Process | Objective | Asset | Task | ProcessingActivity) => {
          const unmonitoredInfo =
            _entity instanceof Control || _entity instanceof Theme ? _entity.unmonitored.toString() : undefined;
          _allEntities.push(getEntity(_entity, unmonitoredInfo));
        },
      );
    } else {
      errorMessage = `${t(localeFilename + ':Title')} : ${_dataToPush.reason}`;
    }

    return errorMessage;
  };

  const loadDataForPicker = async () => {
    if (isLoadingPicker || !canUseContextPicker) return;

    try {
      setIsLoadingPicker(true);
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);

      if (isFollowUpOnlyPicker) {
        const tasksData = await apiGetTasks(true, undefined, undefined, accessToken, appContext.globalDataCache);
        setAllEntities(tasksData.tasks.map((task) => getEntity(task)));

        return;
      }

      const _allControlsPromise = apiGetControls(accessToken, appContext.globalDataCache);
      const _allRisksPromise = apiGetRisks(accessToken, appContext);
      const _allThemesPromise = apiGetThemes(accessToken, appContext.globalDataCache);
      const _allProcessesPromise = apiGetProcesses(accessToken, appContext.globalDataCache);
      const _allObjectivesPromise = apiGetObjectives(accessToken, appContext.globalDataCache);
      const _allAssetsPromise = apiGetAssets(accessToken, appContext.globalDataCache);
      const _allProcessingActivitiesPromise = apiGetProcessingActivities(accessToken, appContext.globalDataCache);

      // Exclude tasks if we're dealing with any type of template.
      const _allTasksPromise = canAddTasks
        ? apiGetTasks(true, undefined, undefined, accessToken, appContext.globalDataCache)
        : Promise.resolve(new Tasks());

      const [
        _allControlsData,
        _allProcessesData,
        _allObjectivesData,
        _allThemesData,
        _allRisksData,
        _allAssetsData,
        _allTasksData,
        _allProcessingActivitiesData,
      ] = await Promise.allSettled([
        _allControlsPromise,
        _allProcessesPromise,
        _allObjectivesPromise,
        _allThemesPromise,
        _allRisksPromise,
        _allAssetsPromise,
        _allTasksPromise,
        _allProcessingActivitiesPromise,
      ]);

      const _allEntities: Entity[] = [];
      let errorMessage = '';

      errorMessage += pushFulfilledData('control', _allEntities, _allControlsData);
      errorMessage += pushFulfilledData('theme', _allEntities, _allThemesData);
      errorMessage += pushFulfilledData('objective', _allEntities, _allObjectivesData);
      errorMessage += pushFulfilledData('process', _allEntities, _allProcessesData);
      errorMessage += pushFulfilledData('risk', _allEntities, _allRisksData);
      errorMessage += pushFulfilledData('asset', _allEntities, _allAssetsData);
      errorMessage += pushFulfilledData('task', _allEntities, _allTasksData);
      errorMessage += pushFulfilledData('processingActivity', _allEntities, _allProcessingActivitiesData);

      if (errorMessage) appContext.setError(errorMessage);

      setAllEntities(_allEntities);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoadingPicker(false);
    }
  };

  // A size-to-content host shows the picker as the block's main action, so its data is fetched as
  // soon as the block mounts: the first keystroke then filters at once instead of waiting for the
  // list calls behind it. EntityPicker's own lazy load sees the entities (or the load in flight)
  // and does not fire a second one.
  useEffect(() => {
    if (props.fitContent && canUseContextPicker) {
      loadDataForPicker();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //
  // Context
  //
  const getColumnsContext = useMemo(() => {
    const columns: IColumn[] = [
      {
        key: 'type',
        name: t('task:Context.List.Type'),
        minWidth: 120,
        maxWidth: 120,
        isResizable: true,
        isMultiline: true,
        onRender: (item?: Entity | TaskRelation) => {
          if (!item) return;
          if (item instanceof Entity) return <IPText>{getEntityName(item, t as TFunction<string[]>)}</IPText>;

          return <IPText>{item.relationText(props.task.taskId, t as TFunction<string[]>)}</IPText>;
        },
      },
      {
        key: 'switch',
        name: t('task:Context.List.Switch'),
        minWidth: canUpdate ? 50 : 1,
        maxWidth: canUpdate ? 70 : 1,
        onRender: (item?: Entity | TaskRelation) => {
          if (!canUpdate) return null;
          if (!(item instanceof TaskRelation)) return null;
          if (!canAddTasks || item.isUnlinked()) return null;

          return (
            <IPIconButton
              iconProps={switchIcon}
              title={t('task:Context.List.Switch')}
              onClick={() => columnCallbacksRef.current.onSwitchTaskRelation(item)}
              styles={{ root: { height: 18 } }}
            />
          );
        },
      },
      {
        key: 'name',
        name: t('task:Context.List.Name'),
        minWidth: 100,
        maxWidth: 400,
        isResizable: true,
        isMultiline: true,
        onRender: (item?: Entity | TaskRelation) => {
          if (!item) return;

          if (item instanceof TaskRelation) {
            if (item.isUnlinked()) {
              const editable = canEditTaskRelation(item);

              return (
                <IPStack
                  horizontal
                  tokens={globalStackTokensGapSmall}
                  verticalAlign="center"
                  onClick={editable ? () => columnCallbacksRef.current.onLinkTaskRelation(item) : undefined}
                  styles={{ root: editable ? { cursor: 'pointer' } : undefined }}
                >
                  <IPText styles={{ root: { fontStyle: 'italic', color: theme.palette.neutralSecondary } }}>
                    {t('task:Context.UnlinkedFollowUp')}
                  </IPText>
                  {editable && (
                    <IPFontIcon
                      iconName={linkIcon.iconName}
                      title={t('task:Context.LinkToTask')}
                      style={{ fontSize: 14, color: theme.palette.themePrimary }}
                    />
                  )}
                </IPStack>
              );
            } else {
              // For outgoing relations, show the target task (taskTo); for incoming, show the source (taskFrom)
              const relatedTask = item.task(props.task.taskId);
              const direction = item.direction(props.task.taskId);
              const isOutgoing = direction === TaskRelationshipDirection.To;
              const hintText =
                direction === TaskRelationshipDirection.From
                  ? t('task:Context.RelationTexts.FromFollowUpHint')
                  : direction === TaskRelationshipDirection.To
                  ? t('task:Context.RelationTexts.ToFollowUpHint')
                  : undefined;

              return (
                <IPStack horizontal tokens={globalStackTokensGapSmall} verticalAlign="center">
                  <IPStack.Item>
                    <IPText>
                      <TaskRelationLinkWithHoverCard task={relatedTask as Task}>
                        <HrefLink
                          href={relatedTask ? `/tasks/${relatedTask.taskId}` : undefined}
                          onClick={() => columnCallbacksRef.current.loadRelatedTaskFromRelation(item)}
                        >
                          {relatedTask?.name}
                        </HrefLink>
                      </TaskRelationLinkWithHoverCard>
                    </IPText>
                    {hintText && (
                      <IPText variant="small" styles={{ root: { color: theme.semanticColors.bodySubtext } }}>
                        {hintText}
                      </IPText>
                    )}
                  </IPStack.Item>
                  {canEditTaskRelation(item) && isOutgoing && (
                    <IPIconButton
                      className={editOnHoverClass}
                      iconProps={editIcon}
                      title={t('task:Context.ChangeLinkedTask')}
                      onClick={() => columnCallbacksRef.current.onLinkTaskRelation(item)}
                      styles={{ root: { height: 18 } }}
                    />
                  )}
                </IPStack>
              );
            }
          }

          return (
            <IPStack horizontal tokens={globalStackTokensGapSmall}>
              {props.navigateExternal && (
                <IPStack.Item>
                  <IPFontIcon
                    {...openExtIcon}
                    style={{ fontSize: 16, color: getFontIconColorPrimary(appContext.useDarkMode, theme) }}
                  />
                </IPStack.Item>
              )}
              <IPStack.Item>
                <IPText>
                  <HrefLink entity={item} external={props.navigateExternal} includeContext>
                    {item.entityName}
                  </HrefLink>
                </IPText>
              </IPStack.Item>
            </IPStack>
          );
        },
      },
    ].filter((column) => {
      return !(column.key === 'switch' && (taskRelations.length === 0 || !canAddTasks));
    });

    if (canUpdate || canUpdateFollowUp) {
      columns.push({
        key: 'remove',
        name: t('task:Context.List.Remove'),
        minWidth: canUpdate || canUpdateFollowUp ? 50 : 1,
        maxWidth: canUpdate || canUpdateFollowUp ? 70 : 1,
        onRender: (item?: Entity | TaskRelation) => {
          if (!item) return null;
          if (item instanceof TaskRelation && !canEditTaskRelation(item)) return null;
          if (item instanceof Entity && !canUpdate) return null;

          return (
            <IPIconButton
              iconProps={deleteIcon}
              onClick={() => {
                columnCallbacksRef.current.onRemove(item);
              }}
              styles={{ root: { height: 18 } }}
            />
          );
        },
      });
    }

    return columns;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    canUpdate,
    canUpdateFollowUp,
    props.task,
    props.navigateExternal,
    canAddTasks,
    appContext.useDarkMode,
    canEditTaskRelation,
    taskRelations.length,
  ]);

  const onRemoveEntity = (entity: Entity) => {
    setIsBusyAddingRemoving(entity.entityId);
    const newTask = props.task.clone();

    switch (entity.typeOfEntity) {
      case EntityTypes.Requirement:
      case EntityTypes.Control: {
        newTask.controlIds = newTask.controlIds?.filter((c) => c !== entity.entityId);
        break;
      }
      case EntityTypes.Process: {
        newTask.processIds = newTask.processIds?.filter((c) => c !== entity.entityId);
        break;
      }
      case EntityTypes.Objective: {
        newTask.objectiveIds = newTask.objectiveIds?.filter((c) => c !== entity.entityId);
        break;
      }
      case EntityTypes.Risk: {
        newTask.riskIds = newTask.riskIds?.filter((c) => c !== entity.entityId);
        break;
      }
      case EntityTypes.Asset: {
        newTask.assetIds = newTask.assetIds?.filter((c) => c !== entity.entityId);
        break;
      }
      case EntityTypes.ProcessingActivity: {
        newTask.processingActivityIds = newTask.processingActivityIds?.filter((c) => c !== entity.entityId);
        break;
      }
      // an internal audit is never task context: see Task.addEntity
    }

    //signal that the form needs to be updated/validated for the removal
    if (props.updateTaskForForm) props.updateTaskForForm(newTask, true);

    //set the removed entity as deleted in the collection
    const idx = entities.findIndex((c) => c.typeOfEntity === entity.typeOfEntity && c.entityId === entity.entityId);
    if (idx >= 0) entities[idx].isDeleted = true;

    const newEntities = entities.map((e) => e.clone());
    setEntities(newEntities);

    if (props.updateTask) {
      newTask.relatedEntities = newEntities;
      props.updateTask(newTask);
    }
  };

  const onRemoveTaskRelation = (relation: TaskRelation) => {
    if (!canEditTaskRelation(relation)) return;

    setIsBusyAddingRemoving(getTaskRelationBusyKey(relation));
    const newTaskRelations = taskRelations.filter((r) => !isSameTaskRelation(r, relation));
    updateTaskRelations(newTaskRelations);
    setIsBusyAddingRemoving(0);
  };

  const onSwitchTaskRelation = (relation: TaskRelation) => {
    try {
      if (!canUpdate || !canAddTasks || relation.isUnlinked()) return;
      if (isBusyAddingRemoving) return;

      setIsBusyAddingRemoving(getTaskRelationBusyKey(relation));
      const newRelations = taskRelations.map((r) => (isSameTaskRelation(r, relation) ? relation.swap() : r));
      const newTask = props.task.clone();
      newTask.taskRelations = newRelations;

      if (props.updateTaskForForm) props.updateTaskForForm(newTask, true);
      updateTaskRelations(newRelations, newTask);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsBusyAddingRemoving(0);
    }
  };

  const onRemove = (item: Entity | TaskRelation) => {
    try {
      if (isBusyAddingRemoving) return;
      if (item instanceof TaskRelation) {
        if (!canEditTaskRelation(item)) return;
        onRemoveTaskRelation(item);
      } else {
        if (!canUpdate) return;
        onRemoveEntity(item);
      }
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsBusyAddingRemoving(0);
    }
  };

  const filterEntities = (all: Entity[], related: (Entity | TaskRelation)[]): Entity[] => {
    return all.filter((e) => {
      const entityIsTask = e.typeOfEntity === EntityTypes.Task;

      if (isFollowUpOnlyPicker && !entityIsTask) return false;
      if (entityIsTask && e.entityId === props.task.taskId) return false;
      const foundControl = related.find((r) => {
        if (r instanceof Entity) return r.entityId === e.entityId && r.typeOfEntity === e.typeOfEntity;
        if (r instanceof TaskRelation)
          return entityIsTask && (r.taskIdFrom === e.entityId || r.taskIdTo === e.entityId);
      });
      if (foundControl) return false;

      return true;
    });
  };

  const addToTask = (entities: Entity[]) => {
    const newTask = props.task.clone();
    newTask.addEntities(entities);

    if (props.updateTask) {
      newTask.relatedEntities = entities.map((e) => e.clone());
      props.updateTask(newTask);
    }
  };

  const onAdd = (entity: Entity) => {
    try {
      if (isLoadingTaskRelations || !props.task) return false;
      if (isBusyAddingRemoving) return;

      setIsBusyAddingRemoving(entity.entityId);
      const newTask = props.task.clone();

      if (entity.typeOfEntity === EntityTypes.Task) {
        if (!canUpdateFollowUp) return false;

        const taskRelation = new TaskRelation(props.task.taskId, entity.entityId, TaskRelationship.FollowUp);
        taskRelation.taskTo = entity.originalObject;

        updateTaskRelations([...taskRelations, taskRelation], newTask);
      } else {
        if (!canUpdate) return false;

        // Default case: entity
        newTask.addEntity(entity);
        if (props.updateTaskForForm) props.updateTaskForForm(newTask, true);

        // If the entity was previously deleted, invert deleted status.
        const matchingEntity = entities.find(
          (e) => e.entityId === entity.entityId && e.typeOfEntity === entity.typeOfEntity,
        );
        const wasDeleted = matchingEntity?.isDeleted;
        if (wasDeleted) {
          matchingEntity.isDeleted = false;
        }
        const newEntities = wasDeleted ? [...entities] : [...entities, entity];
        setEntities(newEntities);

        if (props.updateTask) {
          newTask.relatedEntities = newEntities;
          props.updateTask(newTask);
        }
      }
      // Update newest item to allow for scrolling to the last added item.
      setNewlyAddedItem(entity);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsBusyAddingRemoving(0);
    }
  };

  const onLinkTaskRelation = (relation: TaskRelation) => {
    if (!canEditTaskRelation(relation)) return;

    setLinkRelation(relation);
  };

  // Optimistic relation update, persisted when the task is saved.
  const onLinkToExisting = (relation: TaskRelation, targetTask: Entity) => {
    if (!canEditTaskRelation(relation)) return;

    setLinkRelation(undefined);

    const matchRelation = (r: TaskRelation) => isSameTaskRelation(r, relation);

    const applyLink = (r: TaskRelation): TaskRelation => {
      if (!matchRelation(r)) return r;
      const updated = r.clone();
      updated.taskIdTo = targetTask.entityId;
      // Set the task object so the name renders in the list
      if (targetTask.originalObject instanceof Task) {
        updated.taskTo = targetTask.originalObject;
      }

      return updated;
    };

    const newRelations = taskRelations.map(applyLink);

    // Update local state and task summary so creating another pending follow-up is allowed after this one is linked.
    updateTaskRelations(newRelations);
  };

  const onCreateFromTemplate = (relation: TaskRelation, template: Task) => {
    if (!canEditTaskRelation(relation)) return;

    setLinkRelation(undefined);
    if (props.onLinkFollowUpToTemplate) {
      props.onLinkFollowUpToTemplate(relation, template);
    }
  };

  const onCreateEmptyFollowUpTask = (relation: TaskRelation) => {
    if (!canEditTaskRelation(relation)) return;

    setLinkRelation(undefined);
    if (props.onCreateEmptyFollowUpTask) {
      props.onCreateEmptyFollowUpTask(relation);
    }
  };

  const loadRelatedTaskFromRelation = async (relation: TaskRelation) => {
    try {
      const task = relation.task(props.task.taskId);
      if (!task) return;

      if (props.windowLevel && props.windowLevel >= 1) {
        const url = `/tasks/${task.taskId}`;
        navigateToExternalUrl(url, appContext.user.tenant, appContext.user.login.tenantId);

        return;
      }

      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const relatedTaskCol = await apiGetSingleTask(task.taskId, false, accessToken, appContext.globalDataCache);
      if (relatedTaskCol && relatedTaskCol.tasks.length > 0) {
        setShowRelatedTask(relatedTaskCol.tasks[0]);
      }
    } catch (err) {
      appContext.setError(err);
    }
  };

  // Keep ref in sync with latest callback versions each render
  columnCallbacksRef.current = { onRemove, onLinkTaskRelation, onSwitchTaskRelation, loadRelatedTaskFromRelation };

  const saveTask = (task: Task, isNew: boolean, isCancelled: boolean) => {
    updateTask(task);
  };

  const removeTask = (task: Task) => {
    const newRelations = taskRelations.filter((t) => t.taskIdFrom !== task.taskId && t.taskIdTo !== task.taskId);
    updateTaskRelations(newRelations);
  };

  const updateTask = (task: Task) => {
    const newRelations: TaskRelation[] = [];

    for (let idx = 0; idx < taskRelations.length; idx++) {
      const t = taskRelations[idx];
      if (t.taskFrom && t.taskIdFrom === task.taskId) {
        t.taskFrom = task;
      }
      if (t.taskIdTo && t.taskIdTo === task.taskId) {
        t.taskTo = task;
      }
      newRelations.push(t);
    }

    updateTaskRelations(newRelations);
  };

  // By providing consistent keys, we can correctly use animations on the right items.
  const getRelationKey = (item: Entity | TaskRelation, index?: number): string => {
    const fallback = index ? index.toString() : '';
    if (!item) return fallback;
    if (item instanceof Entity) return item.entityId.toString();

    return `tr-${item.taskTaskId}-${item.taskIdFrom}-${item.taskIdTo ?? 'pending'}`;
  };

  //
  // Main render
  //
  const contextList = (
    <IPShimmeredDetailsList
      className="task-context-list"
      compact
      items={sortedTaskContext}
      shimmerLines={3}
      enableShimmer={isLoadingTaskRelations}
      columns={getColumnsContext}
      isHeaderVisible={!props.hideHeaders}
      selectionMode={IPSelectionMode.none}
      layoutMode={IPDetailsListLayoutMode.justified}
      onRenderDetailsHeader={onRenderDetailsHeaderNoPaddingTopGlobal}
      getKey={getRelationKey}
      enableUpdateAnimations={true}
      onDidUpdate={(detailListProps) => {
        if (!newlyAddedItem || !detailListProps) return;

        const newItemKey = getRelationKey(newlyAddedItem);

        const index = sortedTaskContext.findIndex((item) => {
          const itemKey = getRelationKey(item);

          return itemKey === newItemKey;
        });
        if (index !== -1) {
          const measureItem = (itemIndex: number): number => {
            const cell = document.querySelector(`.task-context-list [data-list-index="${itemIndex}"]`);

            if (cell) {
              const rect = cell.getBoundingClientRect();
              const height = rect.height;

              return height;
            } else return 32.2; // Fallback to a default value if the cell is not found
          };
          detailListProps.scrollToIndex(index, measureItem, ScrollToMode.center);
        }
      }}
    />
  );

  // A size-to-content host (fitContent) gets no fill, no inner scroll pane and no title over an
  // empty list: the host scrolls, and its own hint says what the block is for. Every other host is
  // a tab that hands this component its full height, where the list scrolls on its own.
  return (
    <IPStack verticalFill={!props.fitContent} styles={{ root: { position: 'relative' } }}>
      <IPStack verticalFill={!props.fitContent} tokens={globalStackTokensGapSmall}>
        {(!props.fitContent || sortedTaskContext.length > 0 || isLoadingTaskRelations) && (
          <IPStack.Item>
            <IPText styles={globalTextStylesBold} variant="medium">
              {props.title ?? t('task:Context.Title')}
            </IPText>
          </IPStack.Item>
        )}
        {props.fitContent ? (
          <IPStack.Item>{contextList}</IPStack.Item>
        ) : (
          <IPStack.Item verticalFill styles={{ root: { minHeight: '100px', position: 'relative' } }}>
            <IPScrollablePane scrollbarVisibility={IPScrollbarVisibility.auto}>{contextList}</IPScrollablePane>
          </IPStack.Item>
        )}
        {canUseContextPicker && (
          <IPStack.Item>
            <IPLabel styles={globalTextStylesMarginTopLabelPanel}>
              {isFollowUpOnlyPicker
                ? t('task:Context.SearchExistingTask')
                : canAddTasks
                ? t('task:Context.AddText')
                : t('task:Context.AddTextNoTask')}
            </IPLabel>
          </IPStack.Item>
        )}
        {canUseContextPicker && (
          <IPStack.Item grow={!props.fitContent} styles={props.fitContent ? undefined : { root: { minHeight: 200 } }}>
            <EntityPicker
              className="isoplanner-task-context-search"
              entities={filterEntities(allEntities, sortedTaskContext)}
              addSelectedEntity={onAdd}
              loadData={loadDataForPicker}
              isLoading={isLoadingPicker}
              isOnPanel={false}
              showHeader={false}
              isBusyAdding={isBusyAddingRemoving}
              compact={true}
              extraColumns={[
                {
                  key: 'task-startdate',
                  name: 'startdate', //dummy
                  minWidth: 100,
                  onRender: (item: Entity, index: number | undefined) => {
                    if (!item?.originalObject) return null;

                    return <IPText>{toLocaleDateTimeShort(item.originalObject.startDateTime)}</IPText>;
                  },
                },
                {
                  key: 'task-status',
                  name: 'status', //dummy
                  minWidth: 90,
                  onRender: (item: Entity, index: number | undefined) => {
                    if (!item?.originalObject) return null;
                    const status = appContext.globalDataCache.taskStates.get(item.originalObject.taskStateId);

                    return <IPText>{status ? status?.state : ''}</IPText>;
                  },
                },
              ]}
              disabledEntities={
                props.task.taskType === TaskTypes.Monitoring
                  ? (entity: Entity) => {
                      if (
                        (entity.typeOfEntity === EntityTypes.Control ||
                          entity.typeOfEntity === EntityTypes.Requirement) &&
                        entity.data === 'true'
                      ) {
                        return [true, t('task:Context.MonitoringDisabled')];
                      } else {
                        return [false, undefined];
                      }
                    }
                  : undefined
              }
            />
          </IPStack.Item>
        )}
      </IPStack>
      {showRelatedTask && (
        <SingleTask
          task={showRelatedTask}
          isOpen={showRelatedTask !== undefined}
          close={() => {
            setShowRelatedTask(undefined);
          }}
          onSave={saveTask}
          onRemove={removeTask}
          onUpdate={updateTask}
          windowLevel={(props.windowLevel ?? 0) + 1}
          navigateExternal={props.navigateExternal}
        />
      )}
      {linkRelation && (
        <TaskFollowUpLinkDialog
          isOpen={!!linkRelation}
          task={props.task}
          relation={linkRelation}
          onClose={() => setLinkRelation(undefined)}
          onCreateEmptyTask={onCreateEmptyFollowUpTask}
          onCreateFromTemplate={onCreateFromTemplate}
          onLinkToExisting={onLinkToExisting}
        />
      )}
    </IPStack>
  );
};
